import { addDays, formatClientDate, subDays } from '../../../util/datetime-fns';
import type { PrepackageSearchState } from '../../types';
import { serializeBoundingBox } from './destinationPicker';

export type Month = {
    month: number;
    year: number;
};
export type Duration = {
    from: number;
    to: number;
};

export const dateRangeFor = (baseDate: Date, flexibility: number): string => {
    const startDate = subDays(flexibility, baseDate);
    const endDate = addDays(flexibility, baseDate);
    return `${formatClientDate(startDate)}-${formatClientDate(endDate)}`;
};

export const durationFor = (duration: Duration): string => `${duration.from}-${duration.to}`;

export const monthFor = (month: Month): string => `${`0${month.month}`.slice(-2)}.${month.year}`;

const buildFixedDepartureParams = (state: PrepackageSearchState): (string | null)[] => [
    state.departureDate && state.flexibleDeparture != null
        ? `departureDate=${dateRangeFor(state.departureDate, state.flexibleDeparture)}`
        : null,
    state.returnDate && state.flexibleDeparture != null ? `returnDate=${dateRangeFor(state.returnDate, state.flexibleDeparture)}` : null,
];

const buildFlexibleDepartureParams = (state: PrepackageSearchState): string[] => {
    const allMonths = Array.from({ length: 12 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() + i);
        return `${date.getMonth() + 1}-${date.getFullYear()}`;
    });

    const selected = (state.travelMonths ?? []).map((m) => `${m.month}-${m.year}`);
    const isAllMonthsSelected = selected.length === 12 && allMonths.every((key) => selected.includes(key));

    const params: string[] = [];

    if (!isAllMonthsSelected) {
        params.push(...(state.travelMonths?.map((month) => `travelMonths=${monthFor(month)}`) ?? []));
    }

    if (state.duration) {
        params.push(`duration=${durationFor(state.duration)}`);
    }

    return params;
};

export const buildParams = (state: PrepackageSearchState): string => {
    const params: (string | null)[] = [];

    if (state.originAirport) {
        params.push(`from=${state.originAirport}`);
    }

    if (state.destinations?.length) {
        params.push(...state.destinations.map((destination) => `bbox=${serializeBoundingBox(destination.boundingBox)}`));
        const countries = [
            ...new Set(state.destinations.map((destination) => destination.country).filter((country): country is string => !!country)),
        ];
        params.push(...countries.map((country) => `country=${encodeURIComponent(country)}`));
    } else if (state.boundingBox) {
        params.push(`bbox=${state.boundingBox}`);
        if (state.destinationCountry) {
            params.push(`country=${state.destinationCountry}`);
        }
    }

    if (state.searchType === 'flexible') {
        params.push(...buildFlexibleDepartureParams(state));
    } else {
        params.push(...buildFixedDepartureParams(state));
    }

    if (state.numberOfAdults) {
        params.push(`numberOfAdults=${state.numberOfAdults}`);
    }

    if (state.agesOfChildren?.length) {
        params.push(`agesOfChildren=${state.agesOfChildren.join(',')}`);
    }

    return params.filter((param): param is string => param !== null).join('&');
};
